import type {
  ArcNode,
  NoteNode,
  OmniStudioEdge,
  SceneNode,
} from "@/lib/omnistudio/compiler/directorCompiler.types";
import type {
  ArcSeedProposal,
  BeatSeedProposal,
  DirectionHintProposal,
  SceneSeedProposal,
  WorldNarrativeCompileProposal,
} from "@/lib/world/types/worldProposal.types";
import type {
  MergedProposalReviewCandidate,
  WorldProposalAcceptanceSelection,
  WorldProposalDirectionHintLineage,
  WorldProposalGraphMaterialization,
  WorldProposalLineageRecord,
  WorldProposalReviewSelection,
  WorldProposalStagedVariantRecord,
} from "@/lib/world/review/worldProposalReview.types";
import { resolvePromotionSelection } from "./worldProposalPromotionSelection";

const SCENE_X = 344;
const ARC_X = 84;
const NOTE_X = 642;
const START_Y = 124;
const SCENE_Y_STEP = 108;
const ARC_Y_STEP = 156;
const NOTE_Y_STEP = 132;

export function materializeAcceptedWorldProposalToGraph(
  proposal: WorldNarrativeCompileProposal,
  selection: WorldProposalAcceptanceSelection,
): WorldProposalGraphMaterialization {
  const acceptedScenes = selectAcceptedSceneSeeds(proposal, selection);
  const acceptedBeats = selectAcceptedBeatSeeds(proposal, selection);
  const acceptedArcs = selectAcceptedArcSeeds(proposal, selection);
  const acceptedDirectionHints = selectAcceptedDirectionHints(proposal, selection);

  const directionHintsBySceneId = new Map<string, DirectionHintProposal>(
    acceptedDirectionHints
      .filter((item) => item.targetType === "scene")
      .map((item) => [item.targetId, item] as const),
  );

  const sceneNodes: SceneNode[] = acceptedScenes.map((scene, index) => ({
    id: stableWorldSceneNodeId(scene.sceneId),
    type: "scene",
    title: scene.label,
    x: SCENE_X,
    y: START_Y + index * SCENE_Y_STEP,
    durationHintSec: deriveSceneDuration(scene),
    participants: [],
    mood: scene.emotionalDirection,
  }));

  const arcNodes: ArcNode[] = acceptedArcs.map((arc, index) => ({
    id: stableWorldArcNodeId(arc.arcId),
    type: "arc",
    label: arc.label,
    x: ARC_X,
    y: 88 + index * ARC_Y_STEP,
  }));

  const noteNodes: NoteNode[] = acceptedBeats.map((beat, index) => ({
    id: stableWorldBeatNodeId(beat.beatId),
    type: "note",
    text: `${beat.label}: ${beat.intent}`,
    x: NOTE_X,
    y: 188 + index * NOTE_Y_STEP,
  }));

  const sceneNodesBySceneId = new Map(acceptedScenes.map((scene) => [scene.sceneId, stableWorldSceneNodeId(scene.sceneId)] as const));

  const edges: OmniStudioEdge[] = [
    ...acceptedArcs.flatMap((arc) =>
      arc.relatedSceneIds
        .map((sceneId) => {
          const sceneNodeId = sceneNodesBySceneId.get(sceneId);
          if (!sceneNodeId) return null;

          return {
            id: `world.edge.arc.${arc.arcId}.scene.${sceneId}`,
            from: sceneNodeId,
            to: stableWorldArcNodeId(arc.arcId),
            type: "belongs_to_arc" as const,
          };
        })
        .filter(Boolean) as OmniStudioEdge[],
    ),
    ...acceptedBeats.flatMap((beat) => {
      if (!beat.sceneId) return [];
      const sceneNodeId = sceneNodesBySceneId.get(beat.sceneId);
      if (!sceneNodeId) return [];

      return [{
        id: `world.edge.note.${beat.beatId}.scene.${beat.sceneId}`,
        from: stableWorldBeatNodeId(beat.beatId),
        to: sceneNodeId,
        type: "relates" as const,
      }];
    }),
    ...acceptedScenes.slice(1).map((scene, index) => ({
      id: `world.edge.precedes.${acceptedScenes[index].sceneId}.${scene.sceneId}`,
      from: stableWorldSceneNodeId(acceptedScenes[index].sceneId),
      to: stableWorldSceneNodeId(scene.sceneId),
      type: "precedes" as const,
    })),
  ];

  const lineage: Record<string, WorldProposalLineageRecord> = {};

  for (const scene of acceptedScenes) {
    const graphNodeId = stableWorldSceneNodeId(scene.sceneId);
    lineage[graphNodeId] = {
      graphNodeId,
      sourceProposalId: scene.proposalId,
      sourceWorldId: proposal.worldId,
      confidence: scene.confidence,
      evidence: scene.evidence,
      directionHint: directionHintsBySceneId.has(scene.sceneId)
        ? toDirectionHintLineage(directionHintsBySceneId.get(scene.sceneId) ?? null)
        : null,
    };
  }

  for (const arc of acceptedArcs) {
    const graphNodeId = stableWorldArcNodeId(arc.arcId);
    lineage[graphNodeId] = {
      graphNodeId,
      sourceProposalId: arc.proposalId,
      sourceWorldId: proposal.worldId,
      confidence: arc.confidence,
      evidence: arc.evidence,
      directionHint: null,
    };
  }

  for (const beat of acceptedBeats) {
    const graphNodeId = stableWorldBeatNodeId(beat.beatId);
    lineage[graphNodeId] = {
      graphNodeId,
      sourceProposalId: beat.proposalId,
      sourceWorldId: proposal.worldId,
      confidence: beat.confidence,
      evidence: beat.evidence,
      directionHint: null,
    };
  }

  return {
    nodes: [...arcNodes, ...sceneNodes, ...noteNodes],
    edges,
    lineage,
  };
}

export function materializeWorldProposalPromotionSelectionToGraph(
  proposal: WorldNarrativeCompileProposal,
  selection: WorldProposalAcceptanceSelection,
  reviewSelection?: WorldProposalReviewSelection,
  stagedVariant?: WorldProposalStagedVariantRecord,
  mergedCandidate?: MergedProposalReviewCandidate,
): WorldProposalGraphMaterialization {
  const resolved = resolvePromotionSelection({ reviewSelection, stagedVariant, mergedCandidate });
  if (resolved.source === "baseline") {
    return materializeAcceptedWorldProposalToGraph(proposal, selection);
  }

  if (resolved.source === "merged") {
    const promotedProposal = applyPreferredMergedCandidateToProposal(proposal, resolved.mergedCandidate);
    if (!promotedProposal) {
      return materializeAcceptedWorldProposalToGraph(proposal, selection);
    }

    return materializeAcceptedWorldProposalToGraph(
      promotedProposal,
      includePreferredMergedBase(selection, resolved.mergedCandidate),
    );
  }

  const promotedProposal = applyPreferredStagedVariantToProposal(proposal, resolved.stagedVariant);
  if (!promotedProposal) {
    return materializeAcceptedWorldProposalToGraph(proposal, selection);
  }

  return materializeAcceptedWorldProposalToGraph(
    promotedProposal,
    includePreferredVariantBase(selection, resolved.stagedVariant),
  );
}

function selectAcceptedSceneSeeds(
  proposal: WorldNarrativeCompileProposal,
  selection: WorldProposalAcceptanceSelection,
): SceneSeedProposal[] {
  const acceptedIds = new Set(selection.acceptedSceneProposalIds);
  return proposal.sceneSeeds.filter((item) => acceptedIds.has(item.proposalId));
}

function selectAcceptedBeatSeeds(
  proposal: WorldNarrativeCompileProposal,
  selection: WorldProposalAcceptanceSelection,
): BeatSeedProposal[] {
  const acceptedIds = new Set(selection.acceptedBeatProposalIds);
  return proposal.beatSeeds.filter((item) => acceptedIds.has(item.proposalId));
}

function selectAcceptedArcSeeds(
  proposal: WorldNarrativeCompileProposal,
  selection: WorldProposalAcceptanceSelection,
): ArcSeedProposal[] {
  const acceptedIds = new Set(selection.acceptedArcProposalIds);
  return proposal.arcSeeds.filter((item) => acceptedIds.has(item.proposalId));
}

function selectAcceptedDirectionHints(
  proposal: WorldNarrativeCompileProposal,
  selection: WorldProposalAcceptanceSelection,
): DirectionHintProposal[] {
  const acceptedIds = new Set(selection.acceptedDirectionHintProposalIds);
  return proposal.directionHints.filter((item) => acceptedIds.has(item.proposalId));
}

function applyPreferredStagedVariantToProposal(
  proposal: WorldNarrativeCompileProposal,
  stagedVariant: WorldProposalStagedVariantRecord,
): WorldNarrativeCompileProposal | null {
  if (stagedVariant.proposalType === "scene") {
    let found = false;
    const sceneSeeds = proposal.sceneSeeds.map((item) => {
      if (item.proposalId !== stagedVariant.baseProposalId) return item;
      found = true;
      return {
        ...item,
        label: stagedVariant.label,
        summary: stagedVariant.summary,
      };
    });

    return found ? { ...proposal, sceneSeeds } : null;
  }

  if (stagedVariant.proposalType === "beat") {
    let found = false;
    const beatSeeds = proposal.beatSeeds.map((item) => {
      if (item.proposalId !== stagedVariant.baseProposalId) return item;
      found = true;
      return {
        ...item,
        label: stagedVariant.label,
        intent: stagedVariant.summary,
      };
    });

    return found ? { ...proposal, beatSeeds } : null;
  }

  let found = false;
  const arcSeeds = proposal.arcSeeds.map((item) => {
    if (item.proposalId !== stagedVariant.baseProposalId) return item;
    found = true;
    return {
      ...item,
      label: stagedVariant.label,
      thematicAxis: stagedVariant.summary,
    };
  });

  return found ? { ...proposal, arcSeeds } : null;
}

function applyPreferredMergedCandidateToProposal(
  proposal: WorldNarrativeCompileProposal,
  mergedCandidate: MergedProposalReviewCandidate,
): WorldNarrativeCompileProposal | null {
  if (mergedCandidate.proposalType === "scene") {
    let found = false;
    const sceneSeeds = proposal.sceneSeeds.map((item) => {
      if (item.proposalId !== mergedCandidate.baseProposalId) return item;
      found = true;
      return {
        ...item,
        label: mergedCandidate.label,
        summary: mergedCandidate.summary,
      };
    });

    return found ? { ...proposal, sceneSeeds } : null;
  }

  if (mergedCandidate.proposalType === "beat") {
    let found = false;
    const beatSeeds = proposal.beatSeeds.map((item) => {
      if (item.proposalId !== mergedCandidate.baseProposalId) return item;
      found = true;
      return {
        ...item,
        label: mergedCandidate.label,
        intent: mergedCandidate.summary,
      };
    });

    return found ? { ...proposal, beatSeeds } : null;
  }

  let found = false;
  const arcSeeds = proposal.arcSeeds.map((item) => {
    if (item.proposalId !== mergedCandidate.baseProposalId) return item;
    found = true;
    return {
      ...item,
      label: mergedCandidate.label,
      thematicAxis: mergedCandidate.summary,
    };
  });

  return found ? { ...proposal, arcSeeds } : null;
}

function includePreferredVariantBase(
  selection: WorldProposalAcceptanceSelection,
  stagedVariant: WorldProposalStagedVariantRecord,
): WorldProposalAcceptanceSelection {
  if (stagedVariant.proposalType === "scene") {
    return {
      ...selection,
      acceptedSceneProposalIds: unique([...selection.acceptedSceneProposalIds, stagedVariant.baseProposalId]),
    };
  }

  if (stagedVariant.proposalType === "beat") {
    return {
      ...selection,
      acceptedBeatProposalIds: unique([...selection.acceptedBeatProposalIds, stagedVariant.baseProposalId]),
    };
  }

  return {
    ...selection,
    acceptedArcProposalIds: unique([...selection.acceptedArcProposalIds, stagedVariant.baseProposalId]),
  };
}

function includePreferredMergedBase(
  selection: WorldProposalAcceptanceSelection,
  mergedCandidate: MergedProposalReviewCandidate,
): WorldProposalAcceptanceSelection {
  if (mergedCandidate.proposalType === "scene") {
    return {
      ...selection,
      acceptedSceneProposalIds: unique([...selection.acceptedSceneProposalIds, mergedCandidate.baseProposalId]),
    };
  }

  if (mergedCandidate.proposalType === "beat") {
    return {
      ...selection,
      acceptedBeatProposalIds: unique([...selection.acceptedBeatProposalIds, mergedCandidate.baseProposalId]),
    };
  }

  return {
    ...selection,
    acceptedArcProposalIds: unique([...selection.acceptedArcProposalIds, mergedCandidate.baseProposalId]),
  };
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function deriveSceneDuration(scene: SceneSeedProposal): number {
  if (scene.energyProfile === "high") return 10;
  if (scene.energyProfile === "rising") return 8;
  if (scene.energyProfile === "low") return 12;
  if (scene.energyProfile === "unstable") return 9;
  if (scene.energyProfile === "falling") return 11;
  return 8;
}

function toDirectionHintLineage(
  hint: DirectionHintProposal | null,
): WorldProposalDirectionHintLineage | null {
  if (!hint) return null;

  return {
    emotionalDirection: hint.emotionalDirection,
    intensity: hint.intensity,
    rationale: hint.rationale,
    confidence: hint.confidence,
    sourceProposalId: hint.proposalId,
  };
}

export function stableWorldSceneNodeId(sceneId: string): string {
  return `world.scene.${sceneId}`;
}

export function stableWorldArcNodeId(arcId: string): string {
  return `world.arc.${arcId}`;
}

export function stableWorldBeatNodeId(beatId: string): string {
  return `world.note.${beatId}`;
}